import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { consigneeValidator } from 'src/app/core/custom-validators/add-delivery-validations/add-delivery-validations.validator';
import { nonCanadianPostalValidator } from 'src/app/core/custom-validators/non-canadian-postal/non-canadian-postal.validator';
import { codAmountValidator } from 'src/app/core/custom-validators/non-zero-code-value/non-zero-cod-value.validator';
import { SpecialCharacters } from 'src/app/core/enums';
import { DENSITY, STOP_TYPE } from 'src/app/core/enums/operations.enum';
import { CurrentLocation } from 'src/app/core/models/admin';
import {
  EnterpriseErrorMessage,
  InterceptError,
  InterceptErrorMessage,
  InvalidNumberMessage,
} from 'src/app/core/models/common/api.common.model';
import {
  AddressBook,
  SaveAddressbookRequest,
} from 'src/app/core/models/operations/address-book.model';
import { CreateDeliveryRequest } from 'src/app/core/models/operations/operations-delivery.model';
import { CreatePickupRequest } from 'src/app/core/models/operations/operations-pickup-stop.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { AddressBookService } from 'src/app/core/services/operations/address-book/address-book.service';
import { DeliveryService } from 'src/app/core/services/operations/delivery/delivery.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { TrackingNumberValidatorUtil } from 'src/app/core/utils/tracking-number/tracking-number-validator.utils';
import { DENSITY_LIST } from '../edit-operations-stop/edit-operations-stop.constants';
import {
  ADD_STOP_CONSTANTS,
  AddOperationsStopData,
  AddStopRequest,
  LABELS,
  PackagesType,
  POPUP_TYPE,
  STOP_TIME_TYPE,
} from './add-operations-stop.constants';
import { AddressBookComponent } from './address-book/address-book.component';

@Component({
  selector: 'app-add-operations-stop',
  templateUrl: './add-operations-stop.component.html',
  styleUrls: ['./add-operations-stop.component.scss'],
  standalone: false,
})
export class AddOperationsStopComponent implements OnInit {
  data: AddOperationsStopData = inject(MAT_DIALOG_DATA);
  readonly commonUtils = inject(CommonUtils);
  private readonly destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);
  private readonly dialogRef = inject(MatDialogRef<AddOperationsStopComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly trackNoValidator = inject(TrackingNumberValidatorUtil);
  private readonly deliveryService = inject(DeliveryService);
  private readonly addressBookService = inject(AddressBookService);

  @ViewChild(AddressBookComponent)
  addressBookComponent!: AddressBookComponent;

  readonly CONSTANTS = ADD_STOP_CONSTANTS;
  readonly SpecialCharacters = SpecialCharacters;
  readonly LABELS = LABELS;
  readonly ERRORS = VALIDATION_MESSAGE;
  readonly REGEX = SpecialCharacters.ALPHABET_NUMBER;
  readonly DOT_REGEX = SpecialCharacters.DOT_SPACE_ALLOW;
  readonly ADDRESS_REGEX = SpecialCharacters.ADDRESS;
  readonly NO_SPACES = SpecialCharacters.ALPHABET_NUMBER_NO_SPACE;
  readonly ALPHA_REGEX = SpecialCharacters.ALPHABET_ONLY;
  readonly IMAGES = IMAGE_SRC;
  readonly DENSITY_LIST = DENSITY_LIST.slice().sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  readonly POPUP_TYPE = POPUP_TYPE;
  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly RADIO_LABELS = STOP_TIME_TYPE;

  createDeliveryStop!: CreateDeliveryRequest;
  addStopForm!: FormGroup;
  userId = '';
  locationCode = '';
  countryCode$ = new BehaviorSubject<string>('');
  postalCodeValidation$ = new BehaviorSubject<string>('');
  stateValidation$ = new BehaviorSubject<string>('');
  searchButtonDisabled: boolean = false;
  location$ = new BehaviorSubject<CurrentLocation | undefined>(undefined);
  codRequiredResponse!: string[];

  showAddressBook = false;

  isCodRequired: boolean = false;
  isPackageIntercepted: boolean = false;

  currDate = new Date();
  minDate = new Date(
    this.currDate.getFullYear(),
    this.currDate.getMonth(),
    this.currDate.getDate() - 29,
  );
  maxDate = new Date(
    this.currDate.getFullYear(),
    this.currDate.getMonth(),
    this.currDate.getDate() + 20,
  );

  triggerSearch = false;

  /**
   * Angular lifecycle hook that initializes the component.
   *
   * Behavior:
   * 1) Loads current user details:
   *    - Retrieves `UserService` from `serviceProvider`.
   *    - Subscribes to `getCurrentUser$()` and stores:
   *       - `userId` (loginId),
   *       - `locationCode` (location).
   *
   * 2) Loads current location profile:
   *    - Retrieves `LocationService` from `serviceProvider`.
   *    - Subscribes to `getCurrentLocation$()` and updates:
   *       - `countryCode$`      → ISO country code,
   *       - `postalCodeValidation$` → whether postal code is REQUIRED/OPTIONAL,
   *       - `stateValidation$`  → whether state/province is REQUIRED/OPTIONAL,
   *       - Sets `country` form control to ISO code,
   *       - Emits full profile to `location$`.
   *
   * 3) Initializes the reactive form (`initializeForm()`).
   *
   * 4) Conditionally loads COD service-level codes for deliveries:
   *    - If `stopType` is DELIVERY, fetches COD-required service levels and stores
   *      their codes in `codRequiredResponse`.
   *
   * 5) Sets up Address Book search button logic (`checkForSearchAddressBookButton()`).
   *
   * Cleanup:
   * - Uses `takeUntil(this.destroy$)` to auto-unsubscribe on `ngOnDestroy`.
   */
  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.userId = user.loginId;
          this.locationCode = user.location;
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((profile) => {
        this.countryCode$.next(profile.location?.address?.isoCode ?? '');
        this.postalCodeValidation$.next(
          profile.location?.address?.countryId?.postalCode ?? '',
        );
        this.stateValidation$.next(
          profile.location?.address?.countryId?.polDivOne ?? '',
        );
        this.addStopForm
          ?.get('country')
          ?.setValue(profile.location?.address?.isoCode ?? '');

        this.location$.next(profile);
      });

    this.initializeForm();

    this.packagesFormArray.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });

    // Delivery service for COD response
    if (this.addStopForm.get('stopType')?.value === STOP_TYPE.DELIVERY) {
      this.deliveryService
        .getServiceLevelCods()
        .pipe(take(1))
        .subscribe((vals) => {
          this.codRequiredResponse = vals.response.serviceLevels.map(
            (val) => val.code,
          );
        });
    }

    this.checkForSearchAddressBookButton();
  }

  /**
   * Closes the modal dialog.
   *
   * Behavior:
   * - Calls `dialogRef.close()` with no payload.
   *
   * Note:
   * - Previously used to return structured payloads. Currently simplified due to
   *   functional constraints elsewhere (comment indicates temporary change).
   */
  onClose(): void {
    this.commonUtils.openExitModal(this.addStopForm.dirty);
  }

  /**
   * Initializes (or reuses) the reactive form used for adding/editing stops.
   *
   * Behavior:
   *   - builds a new `FormGroup` with controls:
   *   - `stopType`, `route`, `stopTimeType`
   *   - Consignee details: `consignee`, `number`, `floor`, `suite`, `address`
   *   - Location details: `city`, `state`, `postalCode`, `latitude`, `longitude`, `country`
   *   - Address book and scheduling: `saveToAddressBook`, `planSequence`, `planDate`
   *   - Packages (FormArray of FormGroup):
   *       - `trackingNumber`, `shipperAccount`, `cod`, `declaredValue`
   *       - Applies `trackingNumberValidator` to each package group.
   *
   * Validators:
   * - `stopType`: required
   * - `consignee`: required, length/case regex via `consigneeValidator(25, ...)`
   * - `address`: required, length/case regex via `consigneeValidator(30, ...)`
   * - `city`: required
   * - `state`: required if `stateValidation$` emits 'REQUIRED'; otherwise null validator
   * - `postalCode`: required if `postalCodeValidation$` emits 'REQUIRED',
   *                 plus `nonCanadianPostalValidator(countryCode$.getValue())`
   * - `trackingNumber` and `shipperAccount`: required when popup type is DELIVERY
   *
   * Initialization data sources:
   * - Prefills from `data.trackingNumData` where available; uppercases certain fields (address/state/postalCode).
   * - Sets `country` to current location `countryCode$` if absent in tracking data.
   */
  private initializeForm(): void {
    const validators = [
      this.trackNoValidator.uniqueTrackingNumbersValidator('trackingNumber'),
    ].filter(Boolean) as ValidatorFn[];

    this.addStopForm = this.fb.group({
      stopType: [this.data.type, Validators.compose([Validators.required])],
      route: [this.data.route],
      stopTimeType: [STOP_TIME_TYPE.PLANNED],
      consignee: [
        this.data?.trackingNumData?.consigneeName ?? '',
        Validators.compose([
          Validators.required,
          consigneeValidator(25, /^[a-zA-Z0-9.\-\, ]*$/),
        ]),
      ],
      number: [this.data?.trackingNumData?.consigneeStreetNumber],
      floor: [this.data?.trackingNumData?.consigneeFloorNumber],
      suite: [this.data?.trackingNumData?.consigneeSuiteNumber],
      address: [
        this.data?.trackingNumData?.consigneeAddr1?.toUpperCase() ?? '',
        Validators.compose([
          Validators.required,
          consigneeValidator(30, /^[a-zA-Z0-9.\-\, ]*$/),
        ]),
      ],
      city: [
        this.data?.trackingNumData?.consigneePd2 ?? '',
        Validators.compose([Validators.required]),
      ],
      state: [
        this.data?.trackingNumData?.consigneePd3?.toUpperCase() ?? '',
        this.stateValidation$.getValue() === 'REQUIRED'
          ? Validators.required
          : Validators.nullValidator,
      ],
      postalCode: [
        this.data?.trackingNumData?.consigneePostal?.toUpperCase() ?? '',
        Validators.compose([
          this.postalCodeValidation$.getValue() === 'REQUIRED'
            ? Validators.required
            : Validators.nullValidator,
          nonCanadianPostalValidator(this.countryCode$.getValue()),
        ]),
      ],
      latitude: [this.data?.trackingNumData?.latitude],
      longitude: [this.data?.trackingNumData?.longitude],
      country: [
        this.data?.trackingNumData?.consigneePd1 ??
          this.countryCode$.getValue(),
      ],
      saveToAddressBook: [false],
      planSequence: 1,
      planDate: [{ value: this.data.planDate, disabled: true }],
      packages: this.fb.array(
        [
          this.fb.group(
            {
              trackingNumber: [
                this.data?.trackingNumData?.tracknum ?? '',
                this.data.type === POPUP_TYPE.DELIVERY
                  ? Validators.compose([Validators.required])
                  : null,
              ],
              shipperAccount: [
                this.data?.trackingNumData?.shipperNumber ?? '',
                this.data.type === POPUP_TYPE.DELIVERY
                  ? Validators.compose([Validators.required])
                  : null,
              ],
              cod: ['0'],
              declaredValue: ['0'],
            },
            {
              validators: [
                this.trackNoValidator.trackingNumberValidator('trackingNumber'),
              ],
            },
          ),
        ],
        { validators: Validators.compose(validators) },
      ),
    });
  }

  /**
   * Orchestrates the Add Stop flow:
   *  - Triggers address book save (if selected),
   *  - Builds the `AddStopRequest` from form values,
   *  - Prepares package payloads based on stop type (Delivery vs Pickup),
   *  - Performs COD/interception validations,
   *  - Either halts (if invalid/intercepted) or proceeds with `evaluateAddStopForm(request)`.
   *
   * Flow:
   * 1) Calls `subscribeToSaveAddressBookButton()` to optionally save the address to the address book.
   * 2) Constructs base `AddStopRequest.stop` with user, route, plan date, type, and address details.
   * 3) If DELIVERY:
   *    - Collects package data (uppercase tracking & shipper, COD amount) and assigns to `request.deliveryPackages`.
   *    - Calls `checkValidationsForCod(trackingNumberList)`:
   *       - If COD required + invalid package fields, or interception occurs → set `isCodRequired=false` and do not submit.
   *       - Else, proceed with `evaluateAddStopForm(request)`.
   * 4) If PICKUP:
   *    - Collects package data (uppercase tracking & shipper, declared value) and, if tracking is present, assigns to `request.pickupPackages`.
   *    - Calls `checkValidationsForCod(trackingNumberList)` and performs the same invalid/interception check as above.
   *    - If valid, proceeds with `evaluateAddStopForm(request)`.
   *
   * Side effects:
   *  - Reads/writes multiple form controls,
   *  - Mutates `isCodRequired` depending on validation outcome,
   *  - Uses `packagesFormArray` to iterate package groups.
   */
  async sendAddStopEvent() {
    const addrId = await firstValueFrom(
      this.subscribeToSaveAddressBookButton(),
    );
    const trackingNumberList: string[] = [];
    const packages: PackagesType[] = [];
    const packageControls = this.packagesFormArray?.controls;

    const stopType = this.addStopForm.get('stopType')?.value;
    const isDelivery = stopType === STOP_TYPE.DELIVERY;

    const request: AddStopRequest = {
      stop: {
        loop: this.data.route ?? '',
        userId: this.userId,
        planSequence: 1,
        planDate: new Date(
          formatDate(
            this.addStopForm.get('planDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
        ),
        completed: false,
        density: DENSITY.UNKNOWN,
        stopType: this.addStopForm.get('stopType')?.value as STOP_TYPE,
        address: {
          id: addrId,
          consignee: this.addStopForm.get('consignee')?.value,
          country: this.addStopForm.get('country')?.value,
          postalCode: (
            (this.addStopForm.get('postalCode')?.value ?? '') as string
          ).toUpperCase(),
          state: this.addStopForm.get('state')?.value ?? '',
          city: this.addStopForm.get('city')?.value,
          floor: this.addStopForm.get('floor')?.value,
          suite: this.addStopForm.get('suite')?.value,
          address1: this.addStopForm.get('address')?.value,
          streetParsed: {
            name: this.addStopForm.get('address')?.value,
            number: this.addStopForm.get('number')?.value,
          },
        },
      },
    };

    // For Delivery
    if (isDelivery) {
      packageControls.forEach((data) => {
        packages.push({
          trackingNumber: data.value.trackingNumber.toUpperCase(),
          shipperAccount: data.value.shipperAccount.toUpperCase(),
          paymentDue: {
            amount: data.value.cod,
          },
          position: data.value.position ?? 0,
        });

        trackingNumberList.push(data.value?.trackingNumber);
      });

      (request as any).deliveryDetail = {
        signatureConfirm: '',
      };

      request.deliveryPackages = packages;

      this.checkValidationsForCod(trackingNumberList).subscribe(() => {
        const containsInvalidField = (
          this.addStopForm.get('packages') as FormArray
        ).controls.some((val) => val.invalid);

        if (
          (this.isCodRequired && containsInvalidField) ||
          this.isPackageIntercepted
        ) {
          this.isCodRequired = false;
        } else {
          this.evaluateAddStopForm(request as unknown as CreateDeliveryRequest);
        }
      });
    }

    // For Pickup
    if (!isDelivery) {
      packageControls.forEach((data) => {
        packages.push({
          trackingNumber: data.value?.trackingNumber?.toUpperCase(),
          shipperAccount: data.value?.shipperAccount?.toUpperCase(),
          declaredValue: {
            amount: data.value.declaredValue,
          },
          position: data.value.position ?? 0,
        });

        trackingNumberList.push(data.value?.trackingNumber);
      });

      this.checkValidationsForCod(trackingNumberList).subscribe(() => {
        const containsInvalidField = (
          this.addStopForm.get('packages') as FormArray
        ).controls.some((val) => val.invalid);

        if (
          (this.isCodRequired && containsInvalidField) ||
          this.isPackageIntercepted
        ) {
          this.isCodRequired = false;
        } else {
          const request: AddStopRequest = {
            stop: {
              loop: this.data.route ?? '',
              userId: this.userId,
              planSequence: 1,
              planDate: new Date(
                formatDate(
                  this.addStopForm.get('planDate')?.value,
                  'yyyy-MM-dd',
                  'en-US',
                ),
              ),
              completed: false,
              density: DENSITY.UNKNOWN,
              stopType: this.addStopForm.get('stopType')?.value as STOP_TYPE,
              address: {
                id: addrId,
                consignee: this.addStopForm.get('consignee')?.value,
                country: this.addStopForm.get('country')?.value,
                postalCode: (
                  (this.addStopForm.get('postalCode')?.value ?? '') as string
                ).toUpperCase(),
                state: this.addStopForm.get('state')?.value ?? '',
                city: this.addStopForm.get('city')?.value,
                floor: this.addStopForm.get('floor')?.value,
                suite: this.addStopForm.get('suite')?.value,
                address1: this.addStopForm.get('address')?.value,
                streetParsed: {
                  name: this.addStopForm.get('address')?.value,
                  number: this.addStopForm.get('number')?.value,
                },
              },
            },
            pickupDetail: {
              scanCode:
                this.addStopForm.get('noPackagePickupStatus')?.value ?? '',
              pickupRequestNumber:
                this.addStopForm.get('pickupRequestNumber')?.value ?? '',
              pickupType: this.addStopForm.get('pickupRequestNumber')?.value
                ? 'ONROAD'
                : '',
            },
            pickupPackages:
              packages.length && packages[0].trackingNumber ? packages : [],
          };

          this.evaluateAddStopForm(request as unknown as CreatePickupRequest);
        }
      });
    }
  }

  /**
   * Saves the current address to the Address Book when enabled.
   *
   * Behavior:
   * - Builds a `SaveAddressbookRequest` from the current form values.
   * - Calls `updateValueAndValidity()` on `postalCode` to ensure validators run before saving.
   * - If the `saveToAddressBook` toggle is `true`, invokes `addressBookService.saveToAddressBook(request)` and subscribes.
   *
   * Notes:
   * - The service subscription is fire-and-forget (no success/error handling here).
   * - Intended to be invoked at the start of the Add Stop flow.
   */
  subscribeToSaveAddressBookButton() {
    const form = this.addStopForm;

    const request: SaveAddressbookRequest = {
      route: form.get('route')?.value,
      customername: form.get('consignee')?.value,
      streetnumber: form.get('number')?.value,
      streetname: form.get('address')?.value,
      floor: form.get('floor')?.value,
      suite: form.get('suite')?.value,
      pd1: form.get('state')?.value,
      pd2: form.get('city')?.value,
      postalcode: form.get('postalCode')?.value,
      country: form.get('country')?.value,
    };
    this.addStopForm.get('postalCode')?.updateValueAndValidity();

    if (this.addStopForm.get('saveToAddressBook')?.value !== true) {
      return of(null);
    }
    return this.addressBookService
      .saveToAddressBook(request)
      .pipe(map((res: any) => res.response.addressId));
  }

  /**
   * Validates COD requirements for packages and checks for package interception via backend.
   *
   * COD validation:
   * - Iterates all package groups and inspects `trackingNumber`:
   *   - Extracts `substring(8,10)` as `codCheckSegment`.
   *   - If `codCheckSegment` is in `codRequiredResponse` AND tracking starts with `1Z`:
   *       - Sets `isCodRequired = true`.
   *       - If COD currently equals `'0'`, sets COD to `''` and applies validators:
   *         `Validators.compose([codAmountValidator, Validators.required])`.
   *   - Else:
   *       - Resets COD to `0`, clears validators and errors.
   * - Marks COD controls as touched and updates validity.
   *
   * Interception check:
   * - Calls `deliveryService.validateDeliveryPackage(packages)`:
   *   - If `stopType === DELIVERY` and response has `errors`:
   *       - Calls `handleErrorCheck(res.errors)` and sets `isPackageIntercepted = true`.
   *   - Otherwise sets `isPackageIntercepted = false`.
   *
   * @param packages List of tracking numbers to validate against backend for interceptions.
   * @returns Observable<void> that completes after validation (mapped to `void`).
   *
   * Side effects:
   * - Mutates `isCodRequired` and `isPackageIntercepted`.
   * - Applies/clears validators on `cod` controls within the `packages` FormArray.
   */

  checkValidationsForCod(packages: string[]): Observable<void> {
    const packagesArray = this.addStopForm.get('packages') as FormArray;

    packagesArray.controls.forEach((group) => {
      const trackingNumberControl = group.get('trackingNumber');
      const codControl = group.get('cod');

      // Checking for COD required packages
      // (PTN TRACKING NUMBERS NEED A DIFFERENT CHECK THAT NEEDS TO BE DONE ON BE!)
      // FOR NOW ONLY CHECK THIS WITH 1Z NUMBERS!
      const codCheckSegment = trackingNumberControl?.value.substring(8, 10);
      if (
        this.codRequiredResponse?.includes(codCheckSegment) &&
        trackingNumberControl?.value.substring(0, 2) === '1Z'
      ) {
        this.isCodRequired = true;

        // Adding the condition so that if COD already contains a value, it is not overwritten by the error and empty field
        if (codControl?.value === '0') {
          codControl?.setValue('');
          codControl?.setValidators(
            Validators.compose([codAmountValidator, Validators.required]),
          );
          this.isCodRequired = true;
        }
      } else {
        codControl?.setValue(0);
        codControl?.clearValidators();
        codControl?.setErrors(null);
      }

      codControl?.markAsTouched();
      codControl?.updateValueAndValidity();
    });

    // Checking for Intercepted packages
    return this.deliveryService.validateDeliveryPackage(packages).pipe(
      take(1),
      tap((res) => {
        if (
          this.addStopForm.get('stopType')?.value === STOP_TYPE.DELIVERY &&
          !!res.errors
        ) {
          this.handleErrorCheck(res.errors);
          this.isPackageIntercepted = true;
        } else {
          this.isPackageIntercepted = false;
        }
      }),
      map(() => void 0),
    );
  }

  /**
   * Opens the Address Book panel and triggers a data fetch, if the search button is enabled.
   *
   * Behavior:
   * - Uses `setTimeout` to schedule `addressBookComponent.fetchAddressBook()` on the next macrotask,
   *   ensuring the panel is visible/initialized before fetching.
   *
   * Side effects:
   * - Mutates `showAddressBook`.
   * - Invokes `fetchAddressBook()` on the child component (if present).
   */
  searchAddressBook(): void {
    this.showAddressBook = true;
    this.triggerSearch = true;

    // reset for next click
    setTimeout(() => (this.triggerSearch = false));
  }

  /**
   * Subscribes to changes on key address-related form fields to control the Address Book search button.
   *
   * Behavior:
   * - For each of the fields: `address`, `city`, `consignee`:
   *   - Subscribes to `valueChanges` and calls `checkSearchButtonState()` on each change.
   *
   * Purpose:
   * - Keeps `searchButtonDisabled` up-to-date (enabled when any of the fields is dirty & valid).
   *
   * Cleanup:
   * - Subscriptions are not explicitly torn down here; ensure component-level teardown
   *   (e.g., via `takeUntil(this.destroy$)` if needed) if long-lived.
   */
  checkForSearchAddressBookButton(): void {
    ['address', 'city', 'consignee'].forEach((field) => {
      this.addStopForm
        .get(field)
        ?.valueChanges.subscribe(() => this.checkSearchButtonState());
    });
  }

  /**
   * Computes the enabled/disabled state of the Address Book search button.
   *
   * Rules:
   * - Button becomes enabled (`searchButtonDisabled = false`) if **any** of these is true:
   *   - `address` control: dirty **and** valid,
   *   - `city` control:    dirty **and** valid,
   *   - `consignee` control: dirty **and** valid.
   * - Otherwise, the button remains disabled.
   *
   * Side effects:
   * - Mutates `searchButtonDisabled` based on current form control states.
   */
  checkSearchButtonState(): void {
    this.searchButtonDisabled = false;
  }

  /**
   * Applies an address selection from the Address Book to the form.
   *
   * @param selected - The address entry selected by the user.
   *
   * Behavior:
   * - Copies fields from `selected` (`customername`, `pd1`, `pd2`, `postalcode`,
   *   `streetname`, `streetnumber`, `floor`, `suite`) into corresponding form controls:
   *   `consignee`, `state`, `city`, `postalCode`, `address`, `number`, `floor`, `suite`.
   *
   * Side effects:
   * - Mutates the form controls with normalized address data for the stop.
   */
  handleAddressSelection(selected: AddressBook): void {
    this.addStopForm.get('consignee')?.setValue(selected.customername);
    this.addStopForm.get('state')?.setValue(selected.pd1);
    this.addStopForm.get('city')?.setValue(selected.pd2);
    this.addStopForm.get('postalCode')?.setValue(selected.postalcode);
    this.addStopForm.get('address')?.setValue(selected.streetname);
    this.addStopForm.get('number')?.setValue(selected.streetnumber);
    this.addStopForm.get('floor')?.setValue(selected.floor);
    this.addStopForm.get('suite')?.setValue(selected.suite);
  }

  /**
   * Forces the radio group (`stopTimeType`) to re-emit its current value.
   *
   * Behavior:
   * - Reads the current value of `stopTimeType` and sets it back to the same value.
   *
   * Purpose:
   * - Useful to trigger any subscribers or validation recalculation that depends on setValue.
   */
  updateRadioGroup(): void {
    const currRadioValue = this.addStopForm.get('stopTimeType')?.value;
    this.addStopForm.get('stopTimeType')?.setValue(currRadioValue);
  }

  /**
   * Inserts a new package row at the top of the packages list with default validators.
   *
   * Behavior:
   * - Creates a `FormGroup` with controls:
   *   - `trackingNumber`: required (and validated by `trackingNumberValidator` at group level),
   *   - `shipperAccount`: required,
   *   - `cod`: default string `'0'`,
   *   - `declaredValue`: default string `'0'`.
   * - Inserts the new group at index 0 into `packagesFormArray`.
   * - Calls `autoFocusOnNewInput()` to move focus to the newly added inputs.
   *
   * Side effects:
   * - Mutates `packagesFormArray`.
   * - Triggers UI focus change.
   */
  addPackage(addValidator = true): void {
    const control = this.fb.group(
      {
        trackingNumber: [
          '',
          addValidator ? Validators.compose([Validators.required]) : null,
        ],
        shipperAccount: [
          '',
          addValidator ? Validators.compose([Validators.required]) : null,
        ],
        cod: ['0'],
        declaredValue: ['0'],
      },
      {
        validators:
          this.trackNoValidator.trackingNumberValidator('trackingNumber'),
      },
    );

    this.packagesFormArray.updateValueAndValidity({ onlySelf: true });

    this.packagesFormArray.insert(0, control);
    this.commonUtils.autoFocusOnNewInput(this.packagesFormArray.length * 3);
  }

  /**
   * Removes a package row by index; ensures at least one package row remains.
   *
   * @param index - The zero-based index of the package row to remove.
   *
   * Behavior:
   * - If there is more than one row, simply removes the row at `index`.
   * - If removing the only row, removes it and then **adds a fresh** package row
   *   to maintain the minimum of one input row.
   *
   * Side effects:
   * - Mutates `packagesFormArray`.
   * - May call `addPackage()` if the list becomes empty.
   */
  removePackage(index: number): void {
    if (this.packagesFormArray.length > 1) {
      this.packagesFormArray.removeAt(index);
    } else {
      this.packagesFormArray.removeAt(index);
      this.addPackage(
        this.addStopForm.get('stopType')?.value === STOP_TYPE.DELIVERY,
      );
    }

    this.packagesFormArray.updateValueAndValidity({ onlySelf: true });
  }

  /**
   * Closes the dialog and notifies the caller that the user navigated back.
   *
   * Payload structure:
   * - `request: null` (no submission request),
   * - `planned`: current value of `stopTimeType`,
   * - `back: true` indicates a back action rather than submit.
   */
  onBack(): void {
    this.dialogRef.close({
      request: null,
      planned: this.addStopForm.get('stopTimeType')?.value,
      back: true,
    });
  }

  /**
   * Closes the dialog and returns the finalized Add Stop request to the caller.
   *
   * @param request - The typed request to submit (`CreateDeliveryRequest` or `CreatePickupRequest`).
   *
   * Payload structure:
   * - `request`: the constructed request payload,
   * - `planned`: current value of `stopTimeType`,
   * - `back: false` indicates a submit/forward action.
   */
  evaluateAddStopForm(
    request: CreateDeliveryRequest | CreatePickupRequest,
  ): void {
    this.dialogRef.close({
      request: request,
      planned: this.addStopForm.get('stopTimeType')?.value,
      back: false,
    });
  }

  /**
   * Convenience getter for the packages `FormArray`.
   *
   * @returns The `packages` FormArray from `addStopForm` (may be `undefined` if form not initialized).
   */
  get packagesFormArray(): FormArray {
    return this.addStopForm?.get('packages') as FormArray;
  }

  /**
   * Convenience getter for the package row groups.
   *
   * @returns An array of `FormGroup` rows contained in `packagesFormArray`.
   */
  get rowFormGroup(): FormGroup[] {
    return this.packagesFormArray?.controls as FormGroup[];
  }

  /**
   * Applies backend error checks to package tracking numbers and handles enterprise-level errors.
   *
   * @param res - Array of intercepted/invalid tracking error objects from backend.
   *
   * Behavior:
   * - Extracts:
   *   - `enterpriseErrors`: messages matching `EnterpriseErrorMessage`.
   *   - `interceptErrorNumbers`: tracking numbers from messages starting with `InterceptErrorMessage`.
   *   - `trackingErrorNumbers`: tracking numbers from messages starting with `InvalidNumberMessage`.
   * - If any enterprise errors exist:
   *   - Opens a "Do Not Process" modal via `commonUtils.openDoNotProcessModal(enterpriseErrors)`.
   *   - After modal close, removes any package rows whose tracking numbers match the enterprise errors.
   * - For each package row:
   *   - If its tracking number is in `interceptErrorNumbers` → sets `{ intercepted: true }` error.
   *   - If in `trackingErrorNumbers` → sets `{ invalidTrackingNo: true }` error.
   *
   * Side effects:
   * - May remove package rows.
   * - Mutates `FormControl` errors for `trackingNumber`.
   * - Triggers modal display and subsequent filtering/removal actions.
   */
  private handleErrorCheck(res: InterceptError[]): void {
    const packagesArray = this.addStopForm.get('packages') as FormArray;

    const enterpriseErrors = res.filter(
      (val) => val.message === EnterpriseErrorMessage,
    );
    const interceptErrorNumbers = res
      .filter((val) => val.message.startsWith(InterceptErrorMessage))
      .flatMap((val) => val.trackingNumber.split(','));

    const trackingErrorNumbers = res
      .filter((val) => val.message.startsWith(InvalidNumberMessage))
      .flatMap((val) => val.trackingNumber.split(','));

    if (enterpriseErrors.length > 0) {
      this.commonUtils
        .openDoNotProcessModal(enterpriseErrors)
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          res.forEach((val) => {
            packagesArray.controls.forEach((pkgFormGroup, i) => {
              const trackingNumber = pkgFormGroup.get('trackingNumber')?.value;
              if (val.trackingNumber.includes(trackingNumber)) {
                this.removePackage(i);
              }
            });
          });
        });
    }

    packagesArray.controls.forEach((pkgFormGroup) => {
      const control = pkgFormGroup.get('trackingNumber');

      if (interceptErrorNumbers.includes(control?.value)) {
        control?.setErrors({ intercepted: true });
      }

      if (trackingErrorNumbers.includes(control?.value)) {
        control?.setErrors({ invalidTrackingNo: true });
      }
    });
  }

  /**
   * Handle tracking number changes for a specific row.
   *
   * Applies COD-related validators to the row and refreshes validity.
   * Uses `{ emitEvent: false }` to avoid triggering subscriptions or loops.
   *
   * @param rowIndex - Index of the row in the `packages` FormArray.
   */

  onTrackingChanged(rowIndex: number) {
    const row = (this.addStopForm.get('packages') as FormArray).at(
      rowIndex,
    ) as FormGroup;
    this.applyCodValidatorsForRow(row);
    row.updateValueAndValidity({ emitEvent: false });
    this.addStopForm.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Toggle COD validators on the row's `cod` control based on its tracking number.
   *
   * - When COD is required:
   *   - If the control still has the default zero, clear it to show `required` cleanly.
   *   - Apply `codAmountValidator` and `Validators.required`.
   * - When COD is not required:
   *   - Reset value to `0`, clear validators and errors.
   *
   * All updates are performed with `{ emitEvent: false }` to prevent noisy emissions.
   *
   * @param row - Package row `FormGroup` containing `trackingNumber` and `cod` controls.
   */
  private applyCodValidatorsForRow(row: FormGroup): void {
    const tn = row.get('trackingNumber')?.value as string;
    const codCtrl = row.get('cod');

    if (!codCtrl) return;

    if (this.requiresCod(tn)) {
      const val = codCtrl.value;
      if (val === '0' || val === 0) {
        codCtrl.setValue('', { emitEvent: false });
      }
      codCtrl.setValidators([codAmountValidator, Validators.required]);
      codCtrl.updateValueAndValidity({ emitEvent: false });
    } else {
      codCtrl.setValue(0, { emitEvent: false });
      codCtrl.clearValidators();
      codCtrl.setErrors(null);
      codCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Determine whether COD is required for the given tracking number.
   *
   * Rules:
   * - Must start with '1Z'
   * - Must be at least 10 chars
   * - Segment at positions [8..10) must be present in `codRequiredResponse`
   *
   * @param tn - Raw tracking number (nullable).
   * @returns True if COD is required; otherwise false.
   */
  private requiresCod(tn: string): boolean {
    const v = (tn ?? '').toUpperCase();
    if (
      !v.startsWith('1Z') ||
      v.length < 10 ||
      !this.codRequiredResponse?.length
    )
      return false;
    const seg = v.substring(8, 10);
    return this.codRequiredResponse.includes(seg);
  }
}
